import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../../../components/SEO';
import {
  ArrowLeft, CheckSquare, ExternalLink, ChevronDown,
  KeyRound, ShieldCheck, LayoutDashboard, Users,
  Activity, ListChecks, ShoppingBag, ListTodo,
  MessageCircle, SlidersHorizontal, Columns2, ScrollText,
} from 'lucide-react';

/* â”€â”€â”€ Brand â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const TICKED_TEAL  = '#0D9488';
const TICKED_LIGHT = '#CCFBF1';

/* â”€â”€â”€ Feature data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const features = [
  {
    id: 1, icon: KeyRound, color: '#0D9488', bg: '#F0FDFA',
    title: 'PIN-based Staff Login',
    desc: 'Each team member has their own PIN. No passwords to remember, no shared logins. Just pick your name and tap in your code.',
  },
  {
    id: 2, icon: ShieldCheck, color: '#7C3AED', bg: '#F3EEFF',
    title: 'Role-based Access',
    desc: 'Two roles: Admin and Staff. Admins get full control. Staff get exactly what they need — nothing more.',
  },
  {
    id: 3, icon: LayoutDashboard, color: '#0891B2', bg: '#EFF6FF',
    title: 'Personalised Dashboard',
    desc: 'Every staff member lands on a dashboard built for them. Open tasks, overdue, due today, in-progress, and blocked — all at a glance.',
  },
  {
    id: 4, icon: Users, color: '#D97706', bg: '#FFFBEB',
    title: 'Admin Team Overview',
    desc: 'Admins see a live team workload breakdown: who\'s doing what, how many tasks each person has, and who might need help.',
  },
  {
    id: 5, icon: Activity, color: '#DC2626', bg: '#FEF2F2',
    title: 'Activity Feed',
    desc: 'A real-time log of everything happening across the team. Nothing slips through the cracks.',
  },
  {
    id: 6, icon: ListChecks, color: '#0D9488', bg: '#F0FDFA',
    title: 'Powerful Task Management',
    desc: 'Create tasks with titles, descriptions, priority levels (low → urgent), due dates, labels, and assignees. List or Kanban — your call.',
  },
  {
    id: 7, icon: ShoppingBag, color: '#059669', bg: '#ECFDF5',
    title: 'Order-linked Tasks',
    desc: 'Attach any task directly to a Shopify order. Search by name or number and link in one click. Perfect for fulfilment, returns, and custom requests.',
  },
  {
    id: 8, icon: ListTodo, color: '#2563EB', bg: '#EEF2FF',
    title: 'Checklists',
    desc: 'Break tasks into checklist steps. Track who completed each item and when. Nothing gets missed.',
  },
  {
    id: 9, icon: MessageCircle, color: '#0891B2', bg: '#EFF6FF',
    title: 'Comments & Collaboration',
    desc: 'Leave comments on any task. Keep all context in one place instead of scattered across chat apps.',
  },
  {
    id: 10, icon: SlidersHorizontal, color: '#D97706', bg: '#FFFBEB',
    title: 'Smart Filters & Search',
    desc: 'Filter by status, priority, or assignee. Sort by due date, priority, or creation date. Find what you need instantly.',
  },
  {
    id: 11, icon: Columns2, color: '#7C3AED', bg: '#F3EEFF',
    title: 'Kanban Board View',
    desc: 'Drag and drop tasks across To Do → In Progress → Blocked → Done. Visualise your team\'s flow at a glance.',
  },
  {
    id: 12, icon: ScrollText, color: '#DC2626', bg: '#FEF2F2',
    title: 'Full Audit Log',
    desc: 'Every action — task creation, status changes, staff logins, role changes — is recorded. Always know what happened and who did it.',
  },
];

/* â”€â”€â”€ Shared dark section â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const DarkBg = ({ children, className = '' }) => (
  <section className={`relative overflow-hidden ${className}`} style={{ background: '#0B1426' }}>
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: 'linear-gradient(rgba(13,148,136,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(13,148,136,0.07) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
    }} />
    <div style={{ position: 'absolute', top: '-15%', right: '-8%', width: 560, height: 560, background: 'radial-gradient(circle, rgba(13,148,136,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', bottom: '-15%', left: '-5%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(5,150,105,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
    <div className="relative z-10">{children}</div>
  </section>
);

/* â”€â”€â”€ Top bar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const TickedTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to site
      </Link>

      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: TICKED_TEAL }}>
          <CheckSquare size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Ticked</span>
      </div>

      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

/* â”€â”€â”€ Footer strip â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const TickedFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Ticked · A Triple A Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/ticked/terms" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/ticked/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Privacy</Link>
      </div>
    </div>
  </div>
);

/* â”€â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export const TickedFeaturesPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Ticked — Task Management & Staff Workflows for Shopify"
        description="Ticked is a Shopify app for retail teams to manage daily tasks, staff workflows, and store operations. PIN-based staff login, kanban boards, and order-linked task tracking."
        path="/apps/ticked"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Ticked',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Shopify',
          description: 'Ticked is a task management and staff workflow Shopify app with PIN-based staff login, kanban boards, and order-linked task tracking for retail teams.',
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            price: '0',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Smart Solutions',
            url: 'https://smartsolution.ai',
          },
        }}
      />
      <TickedTopBar />

      {/* â”€â”€ HERO â”€â”€ */}
      <DarkBg>
        <div className="container-custom py-28 md:py-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            {/* Badge */}
            <div className="flex items-center gap-3 mb-8">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 border text-[0.65rem] font-mono tracking-widest uppercase"
                style={{ borderColor: 'rgba(13,148,136,0.35)', color: TICKED_TEAL, backgroundColor: 'rgba(13,148,136,0.08)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ backgroundColor: TICKED_TEAL }} />
                Shopify Embedded App
              </span>
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(13,148,136,0.3)' }} />
            </div>

            {/* Headline */}
            <h1
              className="font-display font-bold text-white mb-6 leading-[1.05]"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
            >
              Your whole team.{' '}
              <span className="italic" style={{ color: TICKED_TEAL }}>Every task.</span>{' '}
              One place.
            </h1>

            {/* Subtitle */}
            <p
              className="text-[#94A3B8] leading-relaxed mb-10 max-w-xl"
              style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)' }}
            >
              Ticked is a task management app embedded inside Shopify. Merchants and store staff manage tasks,
              track work, and collaborate — without ever leaving the admin.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-16">
              <a
                href="#"
                className="inline-flex items-center gap-2.5 px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#95BF47' }}
              >
                <img src="/resources/shopify.png" className="w-4 h-4 object-contain" alt="Shopify" />
                Add to Shopify
              </a>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border transition-colors hover:border-white/40 hover:text-white"
                style={{ borderColor: 'rgba(148,163,184,0.4)', color: '#94A3B8' }}
              >
                See All Features
                <ChevronDown size={14} />
              </a>
            </div>

            {/* Stats */}
            <div
              className="grid grid-cols-3 gap-8 pt-8"
              style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}
            >
              {[
                { num: '12', label: 'Core Features' },
                { num: 'PIN-based', label: 'Staff Login' },
                { num: 'Kanban + List', label: 'Task Views' },
              ].map(({ num, label }) => (
                <div key={label}>
                  <div
                    className="font-display font-bold text-white mb-1"
                    style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.6rem)' }}
                  >
                    {num}
                  </div>
                  <div className="font-mono text-[0.6rem] tracking-widest text-[#64748B] uppercase">{label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom fade */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
          background: 'linear-gradient(to bottom, transparent, #F8F7F4)',
          pointerEvents: 'none',
        }} />
      </DarkBg>

      {/* â”€â”€ PROBLEM STATEMENT â”€â”€ */}
      <section className="bg-[#F8F7F4] py-16 border-b border-[#E2E8F0]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="section-label">The Problem</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A] mb-5"
              style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)' }}
            >
              Busy stores run on{' '}
              <span className="italic text-gold">scattered</span>{' '}
              systems.
            </h2>
            <p className="text-[#374151] text-sm leading-relaxed max-w-lg">
              Post-it notes, WhatsApp threads, spreadsheets — most Shopify teams manage work everywhere
              except where the work actually is. Ticked puts task management right inside Shopify admin,
              so your team stays focused and nothing falls through the cracks.
            </p>
          </motion.div>
        </div>
      </section>

      {/* â”€â”€ FEATURES â”€â”€ */}
      <section id="features" className="section-padding bg-[#F8F7F4] flex-1">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">What Ticked Does</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              Everything your team needs.{' '}
              <span className="italic text-gold">Nothing they don't.</span>
            </h2>
            <p className="text-[#374151] max-w-md mt-4 text-sm leading-relaxed">
              Twelve features built around the real rhythms of store operations — from fulfilment
              tasks to team accountability and everything in between.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {features.map((feat, i) => (
              <motion.div
                key={feat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="bg-white border border-[#E2E8F0] p-8 group hover:border-[#99F6E4] transition-all duration-300"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
              >
                <div className="flex items-start gap-5">
                  <div
                    className="w-12 h-12 flex items-center justify-center shrink-0 border border-[#E2E8F0]"
                    style={{ backgroundColor: feat.bg }}
                  >
                    <feat.icon size={22} style={{ color: feat.color }} />
                  </div>
                  <div>
                    <h3
                      className="font-display text-lg font-bold text-[#0F172A] mb-2 transition-colors duration-300"
                      style={{ '--hover-color': TICKED_TEAL }}
                    >
                      <span className="group-hover:text-[#0D9488] transition-colors duration-300">{feat.title}</span>
                    </h3>
                    <p className="text-[#374151] text-sm leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* â”€â”€ CTA â”€â”€ */}
      <DarkBg className="py-24">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(13,148,136,0.4)' }} />
              <span
                className="font-mono text-[0.65rem] tracking-[0.25em] uppercase"
                style={{ color: TICKED_TEAL }}
              >
                Get Started
              </span>
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(13,148,136,0.4)' }} />
            </div>

            <h2
              className="font-display font-bold text-white mb-6 leading-[1.1]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}
            >
              Ready to get your team{' '}
              <span className="italic" style={{ color: TICKED_TEAL }}>ticked</span>?
            </h2>

            <p className="text-[#94A3B8] mb-10 leading-relaxed max-w-lg mx-auto">
              Install Ticked and give every staff member a clear view of what they need to do —
              right inside Shopify admin.
            </p>

            <a
              href="#"
              className="inline-flex items-center gap-2.5 px-8 py-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#95BF47' }}
            >
              <img src="/resources/shopify.png" className="w-4 h-4 object-contain" alt="Shopify" />
              Add to Shopify — Free to Install
            </a>
          </motion.div>
        </div>
      </DarkBg>

      <TickedFooter />
    </div>
  );
};
